import { getWFSData, getData, createFetchError, postWFSDataSerialized, } from "../../../infra3d/utils/ajax";
import WFS from "ol/format/WFS";
import GeoJSON from "ol/format/GeoJSON";
import IsLike from "ol/format/filter/IsLike";
import EqualTo from "ol/format/filter/EqualTo";
import LessThan from "ol/format/filter/LessThan";
import GreaterThan from "ol/format/filter/GreaterThan";
import { and } from "ol/format/filter";
import { Feature } from "ol";
/**
 * @class Layer
 */
export class WFSGeoserver {
    /**
     * versions
     * WFS 1.1.0 (supported by OL and geoserver)
     * WFS 2.0 (only supported by geoserver, includes paging)
     *
     * @constructor
     */
    constructor(baseurl, project, opt_options) {
        const options = opt_options || {};
        this.baseurl = baseurl;
        this.project = project;
        this.username = options.username;
        this.password = options.password;
        this.version = typeof options.version === "undefined" ? "1.1.0" : options.version;
        this.featureNS = options.featureNS;
        this._withCredentials = typeof options.withCredentials === "undefined" ? true : options.withCredentials;
        this._usePost = typeof options.usePost === "undefined" ? false : options.usePost;
        //remove last / from baseurl
        if (this.baseurl.slice(-1) == "/")
            this.baseurl = this.baseurl.slice(0, this.baseurl.length - 1);
    }
    /**
     * Gets avaible capabilities of current project
     * @param onSuccess
     * @param onError
     */
    getCapabilities(onSuccess, onError) {
        const url = this.baseurl + (this.project != "" ? "/" + this.project + "/wfs" : "");
        const options = {
            withCredentials: this._withCredentials,
            dataType: "xml",
        };
        if (this._usePost) {
            const payload = this._writeGetCapabilities();
            postWFSDataSerialized(url, payload, this.username, this.password, onSuccess, onError, options);
        }
        else {
            const variables = {
                service: "wfs",
                version: "2.0.0",
                request: "GetCapabilities",
            };
            getWFSData(url, variables, this.username, this.password, onSuccess, onError, options);
        }
    }
    /**
     * Gets properties layers of current project
     *
     * @param onSuccess
     * @param onError
     */
    describeFeatureTypes(onSuccess, onError) {
        const url = this.baseurl + (this.project != "" ? "/" + this.project + "/wfs" : "");
        const options = {
            withCredentials: this._withCredentials,
        };
        if (this._usePost) {
            const payload = this._writeDescribeFeatureType();
            postWFSDataSerialized(url, payload, this.username, this.password, onSuccess, onError, options);
        }
        else {
            const variables = {
                service: "wfs",
                version: "2.0.0",
                request: "DescribeFeatureType",
                outputFormat: "application/json",
            };
            getWFSData(url, variables, this.username, this.password, onSuccess, onError, options);
        }
    }
    /**
     * Feature request with options
     *
     * @param options
     * @param onSuccess
     * @param onError
     */
    getFeature(options, onSuccess, onError) {
        const url = this.baseurl + "/" + this.project + "/wfs";
        const options_ = {
            withCredentials: this._withCredentials,
        };
        if (this._usePost) {
            const wfsFormat = new WFS({
                featureNS: this.featureNS + "/" + this.project,
            });
            const payload = wfsFormat.writeGetFeature({
                featureTypes: [options.typeName],
                srsName: options.srsName,
                outputFormat: "JSON",
                geometryName: options.geometryName,
                bbox: options.bbox.split(",").slice(0, 4),
                featureNS: this.featureNS + "/" + this.project,
                featurePrefix: this.project,
            });
            postWFSDataSerialized(url, payload, this.username, this.password, onSuccess, onError, options_);
        }
        else {
            const variables = options;
            getWFSData(url, variables, this.username, this.password, onSuccess, onError, options_);
        }
    }
    /**
     * Feature request with options and additional filter
     *
     * @param options
     * @param filters
     * @param sorter
     * @param onSuccess
     * @param onError
     */
    readFeatures(options, filters, sorter, onSuccess, onError) {
        const url = this.baseurl + "/" + this.project + "/wfs";
        const options_ = {
            withCredentials: this._withCredentials,
        };
        if (this._usePost) {
            const wfsFormat = new WFS({
                featureNS: this.featureNS + "/" + this.project,
            });
            const payload = wfsFormat.writeGetFeature({
                featureTypes: [options.typeName],
                srsName: options.srsName,
                outputFormat: "JSON",
                geometryName: options.geometryName,
                filter: this._createOlFilters(filters),
                maxFeatures: options.count,
                count: options.count,
                startIndex: options.startIndex,
                featureNS: this.featureNS + "/" + this.project,
                featurePrefix: this.project,
            });
            if (sorter.length > 0 && sorter[0].property != "id") {
                //can't sort by id
                payload.getElementsByTagName("Query")[0].appendChild(this._createSorterXML(sorter));
            }
            postWFSDataSerialized(url, payload, this.username, this.password, onSuccess, onError, options_);
        }
        else {
            if (filters.length > 0) {
                options["cql_filter"] = this._createFilterString(filters);
            }
            if (sorter.length > 0 && sorter[0].property != "id") {
                options["sortBy"] = this._createSorterString(sorter);
            }
            const variables = options;
            getWFSData(url, variables, this.username, this.password, onSuccess, onError, options_);
        }
    }
    /**
     * Gets sld style definition of given layer
     *
     * @param featuretype
     * @param onSuccess
     * @param onError
     */
    getStyle(featuretype, onSuccess, onError) {
        if (this._usePost) {
            onSuccess(null);
            return;
        }
        const url = this.baseurl + (this.project != "" ? "/" + this.project + "/wfs" : "");
        const options = {
            withCredentials: this._withCredentials,
            dataType: "xml",
        };
        const urlVars = {
            service: "wms",
            version: "1.1.1",
            request: "GetStyles",
            layers: featuretype,
        };
        getData(url, onSuccess, onError, urlVars, options);
    }
    /**
     * Sends transaction to geoserver
     *
     * @param options
     * @param inserts
     * @param updates
     * @param deletions
     * @param onSuccess
     * @param onError
     */
    applyEdits(options, inserts, updates, deletions, onSuccess, onError) {
        const olFormat = new GeoJSON({
            geometryName: options.geometryName,
            dataProjection: options.srsName,
            featureProjection: options.srsName,
        });
        const rawInserts = olFormat.readFeatures(inserts);
        const ol_inserts = rawInserts.filter((f) => f instanceof Feature);
        const rawUpdates = olFormat.readFeatures(updates);
        const ol_updates = rawUpdates.filter((f) => f instanceof Feature);
        //remove bbox for updates (wfs can't handle unexpected fields)
        ol_updates.forEach(function (feature) {
            feature.unset("bbox");
        });
        const rawDeletions = olFormat.readFeatures(deletions);
        const ol_deletions = rawDeletions.filter((f) => f instanceof Feature);
        const wfsFormat = new WFS();
        const payload = wfsFormat.writeTransaction(ol_inserts, ol_updates, ol_deletions, {
            featureNS: this.featureNS + "/" + this.project,
            featurePrefix: this.project,
            featureType: options.featureType,
            srsName: options.srsName,
            version: "1.1.0",
            nativeElements: [],
        });
        const handleSuccess = (response) => {
            const exception = response.getElementsByTagName("ows:ExceptionText")[0];
            if (exception) {
                const error = createFetchError(0, exception.innerHTML);
                onError(error);
            }
            else {
                onSuccess(wfsFormat.readTransactionResponse(response));
            }
        };
        const url = this.baseurl + "/" + this.project + "/wfs";
        const options_ = {
            withCredentials: this._withCredentials,
            dataType: "xml",
        };
        postWFSDataSerialized(url, payload, this.username, this.password, handleSuccess, onError, options_);
    }
    /**
     * Creates filter querystring
     * @returns {string}
     * @private
     */
    _createFilterString(filters) {
        let cql_filter = "";
        if (filters.length > 0) {
            cql_filter = "(";
            filters.forEach(function (filter) {
                if (cql_filter.length > 1)
                    cql_filter += " AND ";
                switch (filter.operator) {
                    case "like":
                        cql_filter += "(" + filter.property + " ILIKE '" + filter.value + "%25')"; //%25 is url encoded % used as wildcard
                        break;
                    case "eq":
                        cql_filter += "(" + filter.property + " = " + filter.value + ")";
                        break;
                    case "lt":
                        cql_filter += "(" + filter.property + " < " + filter.value + ")";
                        break;
                    case "gt":
                        cql_filter += "(" + filter.property + " > " + filter.value + ")";
                        break;
                }
            });
            cql_filter += ")";
        }
        return cql_filter;
    }
    _createOlFilters(filters) {
        let ol_filter;
        if (filters.length > 0) {
            const conditions = [];
            filters.forEach(function (filter) {
                switch (filter.operator) {
                    case "like":
                        conditions.push(new IsLike(filter.property, filter.value + "%", "%", undefined, undefined, false));
                        break;
                    case "eq":
                        conditions.push(new EqualTo(filter.property, filter.value));
                        break;
                    case "lt":
                        conditions.push(new LessThan(filter.property, filter.value));
                        break;
                    case "gt":
                        conditions.push(new GreaterThan(filter.property, filter.value));
                        break;
                }
            });
            if (conditions.length > 1) {
                ol_filter = and(...conditions);
            }
            else {
                ol_filter = conditions[0];
            }
        }
        return ol_filter;
    }
    _createSorterString(sorters) {
        let sorterString = "";
        if (sorters.length > 0 && sorters[0].property != "id") {
            //can't sort by id
            sorterString += sorters[0].property;
            sorterString += sorters[0].direction == "ASC" ? "+A" : "+D";
        }
        return sorterString;
    }
    _createSorterXML(sorters) {
        const xmlString = "<SortBy></SortBy>";
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlString, "text/xml");
        const sorter_xml = xml.getElementsByTagName("SortBy")[0];
        const property_node = xml.createElement("SortProperty");
        const name_node = xml.createElement("PropertyName");
        const order_node = xml.createElement("SortOrder");
        name_node.appendChild(xml.createTextNode(sorters[0].property));
        order_node.appendChild(xml.createTextNode(sorters[0].direction == "ASC" ? "ASC" : "DESC"));
        property_node.appendChild(name_node);
        property_node.appendChild(order_node);
        sorter_xml.appendChild(property_node);
        return sorter_xml;
    }
    _writeDescribeFeatureType() {
        const xmlString = "<DescribeFeatureType></DescribeFeatureType>";
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlString, "text/xml");
        const version = this.version == "2.0.0" ? "/2.0" : "/" + this.version;
        const describe_xml = xml.getElementsByTagName("DescribeFeatureType")[0];
        describe_xml.setAttribute("xmlns", "http://www.opengis.net/wfs" + (version == "/2.0" ? version : ""));
        describe_xml.setAttribute("service", "WFS");
        describe_xml.setAttribute("maxFeatures", "200");
        describe_xml.setAttribute("xmlns:xsi", "http://www.w3.org/2001/XMLSchema-instance");
        describe_xml.setAttribute("xsi:schemaLocation", "http://www.opengis.net/wfs" +
            (version == "/2.0" ? version : "") +
            " http://schemas.opengis.net/wfs" +
            version +
            "/wfs.xsd");
        describe_xml.setAttribute("version", this.version);
        describe_xml.setAttribute("outputFormat", "application/json");
        return describe_xml;
    }
    _writeGetCapabilities() {
        const xmlString = "<GetCapabilities></GetCapabilities>";
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlString, "text/xml");
        const version = this.version == "2.0.0" ? "/2.0" : "/" + this.version;
        const capabilities_xml = xml.getElementsByTagName("GetCapabilities")[0];
        capabilities_xml.setAttribute("xmlns", "http://www.opengis.net/wfs" + (version == "/2.0" ? version : ""));
        capabilities_xml.setAttribute("service", "WFS");
        capabilities_xml.setAttribute("xmlns:xsi", "http://www.w3.org/2001/XMLSchema-instance");
        capabilities_xml.setAttribute("xsi:schemaLocation", "http://www.opengis.net/wfs" +
            (version == "/2.0" ? version : "") +
            " http://schemas.opengis.net/wfs" +
            version +
            "/wfs.xsd");
        return capabilities_xml;
    }
}
//# sourceMappingURL=wfsgeoserver.js.map